import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ConfirmModal from './shared/ConfirmModal';
import CountersignDialog from './CountersignDialog';
import billingService from '../services/billingService';
import accountsAgentService from '../services/AccountsAgentService';
import { useAuth } from '../contexts/AuthContext';
import { apiErrorMessage } from '../utils/apiError';
import CountrySelect from './CountrySelect';
import { withEnds, scheduleProblem, periodOn, today, nextDay, payerChoices, valueOf, fromValue } from './BillingSchedule';
import { formatDate, formatDateTime } from '../utils/datetime';
import './AddCorporateProfilePage.css';
import './PaymentPorterPage.css';
import './BillingTab.css';

// BILLING — one set of pieces for every screen that shows or asks for billing details:
//
//   BillingTab              the Billing tab on the hotel, chain (both screens), partner (both) and vendor
//   BillingCreateSection    the billing step of the hotel and chain CREATE forms
//   BillingFields / BillToChoice   the parts both are built from
//
// Every entity has the same fields (V564 put them on partners and credential_billing under the names hotel
// and accounts already use). A HOTEL or CHAIN also chooses who pays, and only ever upwards: a hotel itself,
// its chain, its partner or one of the partner's vendors; a chain itself or its partner. Whoever ends up
// paying must have billing details — the lambda refuses a save or a create otherwise; this shows why first.
//
// The lambda decides who may edit (canEdit) and who may touch discount / Stripe ID (canEditRestricted);
// this only reflects it.

const FIELDS = [
  // HIL-141 (V670): the company invoiced, when it is not the name the hotel / chain / partner trades under.
  { key: 'BILLINGNAME', label: 'Billing or Legal Name', hint: 'Used on invoices. Leave empty to use the trading name.' },
  { key: 'BILLINGCONTACT', label: 'Billing Contact' },
  { key: 'BILLINGEMAIL', label: 'Billing Email', type: 'email' },
  { key: 'BILLINGPHONE', label: 'Billing Phone', type: 'tel' },
  { key: 'BILLINGADDRESS1', label: 'Billing Address Line 1' },
  { key: 'BILLINGADDRESS2', label: 'Billing Address Line 2' },
  { key: 'BILLINGADDRESS3', label: 'Billing Address Line 3' },
  { key: 'BILLINGADDRESS4', label: 'Billing City / State' },
  { key: 'BILLINGCOUNTRY', label: 'Billing Country', country: true },
  { key: 'vatno', label: 'VAT Number' },
  { key: 'discount', label: 'Discount', restricted: true },
  { key: 'stripeid', label: 'Stripe ID', restricted: true },
];

// What an invoice cannot be sent without. Keep in step with REQUIRED in ui-v2 billing/db.js.
export const REQUIRED_BILLING = {
  BILLINGCONTACT: 'billing contact',
  BILLINGEMAIL: 'billing email',
  BILLINGADDRESS1: 'billing address line 1',
  BILLINGCOUNTRY: 'billing country',
};

export const missingBilling = (values = {}) => Object.keys(REQUIRED_BILLING)
  .filter(k => !String(values[k] ?? '').trim())
  .map(k => REQUIRED_BILLING[k]);

const SELF = { hotel: 'HOTEL', chain: 'CHAIN' };
const CHOICES = { hotel: ['HOTEL', 'CHAIN', 'PARTNER', 'VENDOR'], chain: ['CHAIN', 'PARTNER'] };

const toForm = (details = {}) => Object.fromEntries(FIELDS.map(f => [f.key, details[f.key] ?? '']));

// Where a payer's details are edited. A superadmin goes to that exact chain / partner; everyone else to
// chain or partner settings, which work on the navbar selection.
const payerLink = (payer, isSuperAdmin) => {
  if (payer.entity === 'chain') return isSuperAdmin ? `/accounts/${payer.id}?tab=billing` : '/chain-settings?tab=details&sub=billing';
  if (payer.entity === 'partner') return isSuperAdmin ? `/partners/${payer.id}?tab=billing` : '/partner-settings?tab=billing';
  if (payer.entity === 'vendor') return `/vendors/${payer.id}?tab=billing`;
  return null;
};

/**
 * Who would pay for a choice and what they lack, from the options the lambda returns:
 * { label, missing } — `missing` empty means the choice can be saved. null for billing itself.
 */
const choiceStatus = (billTo, vendorId, options = {}) => {
  if (billTo === 'CHAIN' && options.chain) {
    return {
      label: options.chain.billedToPartner
        ? `the chain ${options.chain.name}, which is billed to its partner${options.partner ? ` ${options.partner.name}` : ''}`
        : `the chain ${options.chain.name}`,
      missing: options.chain.missing || [],
    };
  }
  if (billTo === 'PARTNER' && options.partner) {
    return { label: `the partner ${options.partner.name}`, missing: options.partner.missing || [] };
  }
  if (billTo === 'VENDOR') {
    const v = (options.vendors || []).find(x => String(x.id) === String(vendorId));
    return v ? { label: `the vendor ${v.name}`, missing: v.missing || [] } : null;
  }
  return null;
};

export const BillingFields = ({ values, onChange, disabled, canEditRestricted, required = false }) => (
  <div className="billing-grid">
    {FIELDS.map(f => {
      const locked = disabled || (f.restricted && !canEditRestricted);
      return (
        <div className="acp-field" key={f.key}>
          <label className="acp-label" htmlFor={`billing-${f.key}`}>
            {f.label}{required && REQUIRED_BILLING[f.key] && <> <span className="acp-required">*</span></>}
          </label>
          {f.country ? (
            <div className="billing-country">
              <CountrySelect name={f.key} value={values[f.key]} onChange={onChange} disabled={locked} />
            </div>
          ) : (
            <input
              id={`billing-${f.key}`}
              className="acp-input"
              type={f.type || 'text'}
              name={f.key}
              value={values[f.key] ?? ''}
              onChange={onChange}
              disabled={locked}
            />
          )}
          {f.restricted && !canEditRestricted && !disabled && (
            <span className="acp-hint">Set by HCONNECT.</span>
          )}
          {f.hint && !disabled && <span className="acp-hint">{f.hint}</span>}
        </div>
      );
    })}
  </div>
);

/** The "Billed to" picker. Offers only the entity itself and the levels above it. */
export const BillToChoice = ({ level, billTo, vendorId, options = {}, onChange, disabled }) => {
  const vendors = options.vendors || [];
  const labelFor = (choice) => {
    if (choice === SELF[level]) return `This ${level}`;
    if (choice === 'CHAIN') return options.chain ? `Chain — ${options.chain.name}` : 'Chain — none';
    if (choice === 'PARTNER') return options.partner ? `Partner — ${options.partner.name}` : 'Partner — none';
    return vendors.length ? 'Vendor' : 'Vendor — the partner has none';
  };
  const unavailable = (choice) => (choice === 'CHAIN' && !options.chain)
    || (choice === 'PARTNER' && !options.partner)
    || (choice === 'VENDOR' && !vendors.length);

  return (
    <div className="acp-row-2">
      <div className="acp-field">
        <label className="acp-label" htmlFor={`billing-bill-to-${level}`}>Billed to</label>
        <select
          id={`billing-bill-to-${level}`}
          className="acp-select"
          value={billTo}
          onChange={(e) => onChange({ billTo: e.target.value, vendorId: e.target.value === 'VENDOR' ? vendorId : '' })}
          disabled={disabled}
        >
          {CHOICES[level].map(c => <option key={c} value={c} disabled={unavailable(c)}>{labelFor(c)}</option>)}
        </select>
        <span className="acp-hint">Who this {level}'s invoices go to — itself or a level above it.</span>
      </div>

      {billTo === 'VENDOR' && (
        <div className="acp-field">
          <label className="acp-label" htmlFor={`billing-vendor-${level}`}>Vendor <span className="acp-required">*</span></label>
          <select
            id={`billing-vendor-${level}`}
            className="acp-select"
            value={vendorId}
            onChange={(e) => onChange({ billTo, vendorId: e.target.value })}
            disabled={disabled}
          >
            <option value="">— Select vendor —</option>
            {vendors.map(v => (
              <option key={v.id} value={v.id}>{v.name}{v.system_name ? ` (${v.system_name})` : ''}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

const PayerProblem = ({ status, level }) => (
  status && status.missing.length > 0 ? (
    <div className="acp-error-banner">
      {status.label.charAt(0).toUpperCase() + status.label.slice(1)} has no complete billing details — missing{' '}
      {status.missing.join(', ')}. Add them on its Billing tab first, or bill this {level} somewhere else.
    </div>
  ) : null
);

/**
 * The billing step of a hotel or chain CREATE form.
 *
 *   level      'hotel' | 'chain'
 *   accountId  the new hotel's chain        partnerId  the new chain's partner
 *   value      { billTo, billToCredentialId, details } — sent to the create as `billing`
 *   onChange   (value) => …
 *   onProblem  (message | null) => … — why the create would be refused, so the form can stop first
 */
export const BillingCreateSection = ({ level, accountId, partnerId, value, onChange, onProblem }) => {
  const [options, setOptions] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const parentEntity = level === 'hotel' ? 'chain' : 'partner';
  const parentId = level === 'hotel' ? accountId : partnerId;

  // The options come from the parent: a new hotel's chain says what its chain, partner and vendors could
  // pay; a new chain's partner says whether it could.
  useEffect(() => {
    if (!parentId) { setOptions({}); return; }
    let cancelled = false;
    setLoadError(null);
    billingService.get(parentEntity, parentId)
      .then(res => {
        if (cancelled) return;
        if (level === 'hotel') {
          const chainPaysViaPartner = res.billTo === 'PARTNER' && !!res.options?.partner;
          setOptions({
            chain: {
              id: res.id,
              name: res.name,
              billedToPartner: chainPaysViaPartner,
              missing: chainPaysViaPartner ? (res.options.partner.missing || []) : (res.missing || []),
            },
            partner: res.options?.partner || null,
            vendors: res.options?.vendors || [],
          });
        } else {
          setOptions({ partner: { id: res.id, name: res.name, missing: res.missing || [] } });
        }
      })
      .catch(err => { if (!cancelled) setLoadError(apiErrorMessage(err, 'Failed to load billing options')); });
    return () => { cancelled = true; };
  }, [level, parentEntity, parentId]);

  const billTo = value.billTo || SELF[level];
  const billsItself = billTo === SELF[level];
  const status = billsItself ? null : choiceStatus(billTo, value.billToCredentialId, options || {});
  const ownMissing = billsItself ? missingBilling(value.details) : [];

  const problem = billsItself
    ? (ownMissing.length ? `Billing details are required: add the ${ownMissing.join(', ')}.` : null)
    : billTo === 'VENDOR' && !value.billToCredentialId
      ? 'Choose the vendor this hotel is billed to.'
      : status && status.missing.length
        ? `${status.label.charAt(0).toUpperCase() + status.label.slice(1)} has no complete billing details — missing ${status.missing.join(', ')}.`
        : null;

  useEffect(() => { if (onProblem) onProblem(problem); }, [problem]); // eslint-disable-line

  const handleField = (e) => {
    const { name, value: v } = e.target;
    onChange({ ...value, details: { ...(value.details || {}), [name]: v } });
  };

  return (
    <div className="form-section billing-tab">
      <h3>Billing</h3>
      {loadError && <div className="acp-error-banner">{loadError}</div>}
      <BillToChoice
        level={level}
        billTo={billTo}
        vendorId={value.billToCredentialId ? String(value.billToCredentialId) : ''}
        options={options || {}}
        onChange={({ billTo: b, vendorId }) => onChange({ ...value, billTo: b, billToCredentialId: vendorId || null })}
        disabled={!options}
      />
      {billsItself ? (
        <BillingFields values={toForm(value.details)} onChange={handleField} disabled={false} canEditRestricted={false} required />
      ) : (
        <PayerProblem status={status} level={level} />
      )}
    </div>
  );
};

// THE QUICKBOOKS SIDE (V567). The hub's details save at once; QuickBooks follows only when an accounts approver
// (Jamie or David) approves the agent's proposal — a new customer for a record with none, an update for a linked
// one. This line says which of those is waiting, so "Saved" is not read as "QuickBooks has it".
const QB_ACTION = { create_billing_customer: 'a new QuickBooks customer', update_billing_customer: 'an update to its QuickBooks customer' };
const QuickBooksStatus = ({ qb }) => {
  if (!qb) return null;
  const linked = qb.customerIds && qb.customerIds.length
    ? `QuickBooks customer ${qb.customerIds.join(', ')}`
    : 'No QuickBooks customer yet';
  const waiting = (qb.pending || []).map(p => `${QB_ACTION[p.actionType] || p.actionType} (proposal #${p.id})`);
  return (
    <p className="billing-muted billing-qb">
      {linked}.{' '}
      {waiting.length
        ? <>Waiting for an accounts approver: {waiting.join('; ')}.</>
        : qb.queued
          ? <>Your change is queued — the accounts agent will propose the QuickBooks side within 15 minutes.</>
          : null}
    </p>
  );
};

// A CHAIN'S OR HOTEL'S QUICKBOOKS CUSTOMER ID (2026-10-27) — for its partner's people (data.canEditQuickBooks). Its own
// field and its own Save, apart from the billing details: setting it only links the record to the customer QuickBooks
// already has, so the accounts agent is told nothing and proposes nothing (it also withdraws an undecided "create a
// QuickBooks customer" proposal for this chain). Several ids, comma-separated, for a chain billed through more than one.
const QuickBooksCustomerField = ({ entity, entityId, customerIds, onSaved }) => {
  const initial = (customerIds || []).join(', ');
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  useEffect(() => { setValue(initial); }, [initial]);
  const save = async () => {
    setBusy(true); setErr(null); setNote(null);
    try {
      const ids = value.split(',').map(x => x.trim()).filter(Boolean);
      onSaved(await billingService.save(entity, entityId, { quickBooksCustomerIds: ids }));
      setNote(ids.length ? 'Saved — linked to QuickBooks. Nothing is sent to the accounts agent for approval.' : 'Saved — no QuickBooks customer.');
    } catch (e) {
      setErr(apiErrorMessage(e, 'The QuickBooks customer id could not be saved.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="billing-section partner-only-panel">
      <div className="acp-field">
        <label className="acp-label" htmlFor="billing-qbo-customer">QuickBooks customer id</label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input id="billing-qbo-customer" className="acp-input" value={value} inputMode="numeric"
            placeholder="e.g. 1234" onChange={(e) => { setValue(e.target.value); setNote(null); }} disabled={busy} />
          <button type="button" className="pp-rules-add-btn" onClick={save} disabled={busy || value.trim() === initial}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
        <p className="billing-muted" style={{ margin: 0 }}>
          The customer's number in QuickBooks (several, comma-separated). Saving it links this {entity} to that customer
          without proposing anything to the accounts agent.
        </p>
      </div>
      {note && <p className="billing-saved">{note}</p>}
      {err && <div className="acp-error-banner">{err}</div>}
    </div>
  );
};

// A BILLING PERIOD'S OWN FIELDS (V663) — what an order form for it needs beyond who pays and when. Names as the API
// sends them (/opt/utils/billing PERIOD_FIELDS); `type` for the input.
const PERIOD_FIELDS = [
  // No contact here: a document's contact is the Billing Contact / Billing Email below (V664).
  { key: 'authorisedPerson', label: 'Authorised person' },
  { key: 'authorisedTitle', label: 'Authorised person’s title' },
  // `default`: what a new period starts with and a blank one is saved as (/opt/utils/billing PERIOD_DEFAULTS, V665).
  { key: 'paymentMethod', label: 'Payment method', default: 'Bank Transfer' },
  { key: 'paymentTerm', label: 'Payment term', default: '30 days' },
  { key: 'frequency', label: 'Frequency', default: 'Annually in advance' },
];
const PERIOD_DEFAULTS = Object.fromEntries(PERIOD_FIELDS.filter(f => f.default).map(f => [f.key, f.default]));
const periodFieldsOf = (p) => Object.fromEntries(PERIOD_FIELDS.map(f => [f.key, (p && p[f.key]) || '']));

// The schedule as the lambda last returned it; a hotel with none yet is its current payer from today. `_k` keeps a
// period's identity on screen while its From date is being changed (never sent).
let periodKeySeq = 0;
const newPeriodKey = () => `p${++periodKeySeq}`;
const savedPeriods = (res) => ((res && res.periods && res.periods.length)
  ? res.periods
  : [{ from: today(), billTo: (res && res.billTo) || 'HOTEL', billToCredentialId: (res && res.billToCredentialId) || null }])
  .map((p, i, all) => ({
    _k: newPeriodKey(), from: p.from, billTo: p.billTo, billToCredentialId: p.billToCredentialId || null,
    ...periodFieldsOf(p),
    // The last period's stored end (V662); every other period's To follows from the next one's start.
    ...(i === all.length - 1 && p.to ? { end: p.to } : {}),
  }));
// What makes a schedule different from the saved one — the To typed on the last period included (`end`), so the page's
// Update button sees it and reports why it cannot be saved yet, rather than saving the hotel and dropping it.
const periodsKey = (list) => JSON.stringify(withEnds(list).map(p => [p.from, p.billTo, p.billToCredentialId || null, p.end || null,
  ...PERIOD_FIELDS.map(f => (p[f.key] || '').trim())]));

// WHO IS BILLED, BY DATE — the hotel's schedule as a list: From, To (empty on the last: it runs on), Billed to.
// Clicking a row opens that period on its own, in place of the list.
const PeriodList = ({ periods, labelOf, selectedKey, onSelect }) => {
  const rows = withEnds(periods);
  const current = periodOn(rows);
  return (
    <table className="bsch-table billing-periods">
      <thead><tr><th>From</th><th>To</th><th>Billed to</th></tr></thead>
      <tbody>
        {rows.map((p, k) => {
          const isCurrent = current && current._k === p._k;
          const pick = () => onSelect(selectedKey === p._k ? null : p._k);
          return (
            <tr key={p._k}
              className={`billing-period-row${isCurrent ? ' bsch-current' : ''}${selectedKey === p._k ? ' billing-period-selected' : ''}`}
              onClick={pick}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } }}
              tabIndex={0}
              aria-selected={selectedKey === p._k}
              title="Show this billing period">
              <td>{k === 0 ? `${formatDate(p.from)} (and before)` : formatDate(p.from)}</td>
              <td>{p.to ? formatDate(p.to) : <span className="billing-muted">—</span>}</td>
              <td>
                {labelOf(p)}
                {isCurrent && <span className="bsch-now">current</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// CANCEL / SAVE, as STYLE.md draws them (Cancel left of Save, a spinner while saving) — the tab's own footer, shown
// only where it is not embedded (settings screens, where every tab saves itself). On the hotel, chain and partner
// pages the page's Update button saves the tab, billing periods included.
export const SaveCancel = ({ saving, blocked, onCancel, onSave }) => (
  <div className="billing-footer">
    <button type="button" className="pp-criteria-add-btn" onClick={onCancel} disabled={saving}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
        <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
      Cancel
    </button>
    <button type="button" className="pp-rules-add-btn" onClick={onSave} disabled={saving || blocked}>
      {saving
        ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>Saving…</>
        : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>Save</>
      }
    </button>
  </div>
);

// The PDF arrives in the response (base64) — saved as a file under the document's own name.
const downloadPdf = (base64, fileName) => {
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'document.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

// WHERE A DOCUMENT IS (V666) — the stage as a badge, and what the caller can do next.
const DOC_STAGES = {
  GENERATED: { label: 'To sign', className: 'billing-doc-badge-amber' },
  HOTEL_SIGNED: { label: 'Signed by the hotel', className: 'billing-doc-badge-blue' },
  COUNTERSIGNED: { label: 'Countersigned', className: 'billing-doc-badge-green' },
};

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

// THE DOCUMENT ROWS — one table for the hotel's "Documents to sign" and a billing period's Documents. Each document
// carries what THIS caller may do with it (the lambda decides): `download` (which copy, or none), `canUploadSigned`
// (the hotel's admins, before signing), `canCountersign` (the partner's admins, once signed), `canDelete`.
//   To sign            → Download (the original), Upload signed copy
//   Signed by hotel    → the hotel can no longer download; the partner downloads the signed copy and Countersigns
//   Countersigned      → only the countersigned copy downloads, for both
const DocumentRows = ({ hotelId, partnerId, docs, onChange, showPeriod }) => {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const fileInput = useRef(null);
  const [uploadFor, setUploadFor] = useState(null);

  const replace = (doc) => onChange(docs.map(d => (d.id === doc.id ? doc : d)));

  const download = async (doc) => {
    setError(null);
    try {
      const res = await billingService.documentLink(hotelId, doc.id);
      downloadPdf(res.pdfBase64, res.fileName || doc.fileName);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not download the document'));
    }
  };

  // Upload: a presigned PUT straight to S3 (a scan can be several MB), then the lambda checks and keeps it.
  const pickFile = (doc) => { setUploadFor(doc); if (fileInput.current) { fileInput.current.value = ''; fileInput.current.click(); } };
  const upload = async (file) => {
    const doc = uploadFor;
    if (!doc || !file) return;
    setBusy(doc.id);
    setError(null);
    try {
      if (file.type && file.type !== 'application/pdf') throw new Error('The signed copy must be a PDF.');
      const { url, key, maxBytes } = await billingService.documentAction(hotelId, doc.id, { action: 'upload-url' });
      if (maxBytes && file.size > maxBytes) throw new Error(`The signed copy must be at most ${Math.round(maxBytes / 1024 / 1024)} MB.`);
      const put = await fetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/pdf' }, body: file });
      if (!put.ok) throw new Error(`The upload failed (HTTP ${put.status}).`);
      const res = await billingService.documentAction(hotelId, doc.id, { action: 'signed', key });
      replace(res.document);
    } catch (err) {
      setError(apiErrorMessage(err, err.message || 'Could not upload the signed copy'));
    } finally {
      setBusy(null);
      setUploadFor(null);
    }
  };

  // SUPERADMIN: take the hotel's upload back — the document returns to "To sign".
  const removeSigned = async (doc) => {
    setConfirm(null);
    setBusy(doc.id);
    setError(null);
    try {
      const res = await billingService.documentAction(hotelId, doc.id, { action: 'remove-signed' });
      replace(res.document);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not remove the signed copy'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (doc) => {
    setConfirm(null);
    setError(null);
    try {
      await billingService.deleteDocument(hotelId, doc.id);
      onChange(docs.filter(d => d.id !== doc.id));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete the document'));
    }
  };

  const stageText = (d) => (d.status === 'COUNTERSIGNED' && d.countersignedAt
    ? `Countersigned ${formatDate(String(d.countersignedAt).slice(0, 10))}${d.countersignedBy ? ` by ${d.countersignedBy}` : ''}`
    : d.status === 'HOTEL_SIGNED' && d.signedAt
      ? `Signed by the hotel ${formatDate(String(d.signedAt).slice(0, 10))}`
      : (DOC_STAGES[d.status] || DOC_STAGES.GENERATED).label);

  return (
    <>
      <input ref={fileInput} type="file" accept="application/pdf,.pdf" hidden
        onChange={e => upload(e.target.files && e.target.files[0])} />
      <table className="bsch-table billing-documents-table">
        <thead>
          <tr>
            <th>Document</th>
            {showPeriod && <th>Billing period</th>}
            <th>Generated</th>
            <th>Status</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {docs.map(d => (
            <tr key={d.id}>
              <td>{d.templateName}</td>
              {showPeriod && <td>{formatDate(d.periodFrom)} – {d.periodTo ? formatDate(d.periodTo) : 'no end'}</td>}
              <td>{formatDateTime(d.createdAt)}</td>
              <td><span className={`billing-doc-badge ${(DOC_STAGES[d.status] || DOC_STAGES.GENERATED).className}`}>{stageText(d)}</span></td>
              <td className="billing-documents-actions">
                <div className="billing-documents-actions-row">
                {d.download && (
                  <button type="button" className="pp-criteria-add-btn" onClick={() => download(d)} disabled={busy === d.id}>
                    {d.download === 'signed' ? 'Download signed copy' : 'Download'}
                  </button>
                )}
                {!d.download && d.status === 'HOTEL_SIGNED' && (
                  <span className="billing-muted">Waiting for the partner to countersign</span>
                )}
                {d.canUploadSigned && (
                  <button type="button" className="pp-rules-add-btn" onClick={() => pickFile(d)} disabled={busy === d.id}>
                    {busy === d.id ? 'Uploading…' : 'Upload signed copy'}
                  </button>
                )}
                {d.canCountersign && partnerId && (
                  <button type="button" className="pp-rules-add-btn partner-only" disabled={busy === d.id}
                    onClick={() => setConfirm({ kind: 'countersign', doc: d })}>
                    Countersign
                  </button>
                )}
                {d.canRemoveSigned && (
                  <button type="button" className="pp-criteria-add-btn pp-criteria-add-btn-danger sa-only" disabled={busy === d.id}
                    onClick={() => setConfirm({ kind: 'remove-signed', doc: d })}>
                    Remove signed copy
                  </button>
                )}
                {d.canDelete && (
                  <button type="button" className="bsch-icon-btn billing-documents-delete partner-only" title="Delete this document"
                    aria-label="Delete this document" onClick={() => setConfirm({ kind: 'delete', doc: d })}>
                    <TrashIcon />
                  </button>
                )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {error && <div className="acp-error-banner">{error}</div>}
      {/* In a portal: the tab sits inside the hotel <form>, and ConfirmModal's buttons would submit it. */}
      {/* COUNTERSIGN: the signed copy with the partner's signatures to place on it (CountersignDialog). */}
      {confirm && confirm.kind === 'countersign' && (
        <CountersignDialog hotelId={hotelId} partnerId={partnerId} doc={confirm.doc}
          onClose={() => setConfirm(null)}
          onDone={(doc) => { setConfirm(null); replace(doc); }} />
      )}
      {confirm && confirm.kind === 'remove-signed' && createPortal(
        <ConfirmModal
          title="Remove signed copy"
          message={`Remove the hotel's signed copy of "${confirm.doc.templateName}"? The document goes back to "To sign", so the hotel can download it and upload a signed copy again.`}
          confirmLabel="Remove"
          danger
          onConfirm={() => removeSigned(confirm.doc)}
          onCancel={() => setConfirm(null)}
        />,
        document.body,
      )}
      {confirm && confirm.kind === 'delete' && createPortal(
        (
          <ConfirmModal
            title="Delete document"
            message={`Delete "${confirm.doc.templateName}" generated ${formatDateTime(confirm.doc.createdAt)}? It is removed with its signed copies.`}
            confirmLabel="Delete"
            danger
            onConfirm={() => remove(confirm.doc)}
            onCancel={() => setConfirm(null)}
          />
        ),
        document.body,
      )}
    </>
  );
};

// ONE PERIOD'S DOCUMENTS (V663) — on a billing period's screen (partner-only, teal): generate another from the partner's
// templates, and the period's documents with their stage. Generated from what is SAVED (ui-v2-billing-documents reads
// the period, the price lines and the payer from the database), so a period with unsaved changes says so first.
const PeriodDocuments = ({ hotelId, saved, unsaved }) => {
  const [data, setData] = useState(null);
  const [templateId, setTemplateId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!saved) return;
    let live = true;
    setData(null);
    billingService.listDocuments(hotelId, saved)
      .then(res => {
        if (!live) return;
        setData(res);
        if (res.templates && res.templates.length === 1) setTemplateId(String(res.templates[0].id));
      })
      .catch(err => { if (live) setError(apiErrorMessage(err, 'Could not load this period’s documents')); });
    return () => { live = false; };
  }, [hotelId, saved]);

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await billingService.generateDocument(hotelId, saved, Number(templateId));
      setData(prev => ({ ...prev, documents: [res.document, ...((prev && prev.documents) || [])] }));
      downloadPdf(res.pdfBase64, res.document.fileName);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not generate the document'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="billing-section billing-documents partner-only-panel">
      <h3 className="billing-payer-title">Documents</h3>
      {!saved ? (
        <p className="billing-muted">Update the hotel to save this billing period first — documents are made from what is saved.</p>
      ) : !data ? (
        error ? <div className="acp-error-banner">{error}</div> : <p className="billing-muted">Loading documents…</p>
      ) : (
        <>
          {data.canGenerate && (
            data.templates && data.templates.length ? (
              <div className="billing-documents-generate">
                <select className="acp-select" value={templateId} onChange={e => setTemplateId(e.target.value)} aria-label="Template">
                  <option value="">Choose a template…</option>
                  {data.templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <button type="button" className="pp-rules-add-btn" onClick={generate} disabled={!templateId || busy || unsaved}
                  title={unsaved ? 'Update the hotel first — the document is made from what is saved' : undefined}>
                  {busy ? 'Generating…' : 'Generate PDF'}
                </button>
              </div>
            ) : (
              <p className="billing-muted">The hotel’s partner has no billing templates yet (Partner Settings › Templates › Billing).</p>
            )
          )}
          {unsaved && data.canGenerate && (
            <p className="billing-muted">There are unsaved changes — update the hotel before generating, so the document shows them.</p>
          )}
          {data.documents && data.documents.length ? (
            <DocumentRows hotelId={hotelId} partnerId={data.partnerId} docs={data.documents}
              onChange={(documents) => setData(prev => ({ ...prev, documents }))} />
          ) : (
            <p className="billing-muted">No documents for this billing period yet.</p>
          )}
          {error && <div className="acp-error-banner">{error}</div>}
        </>
      )}
    </div>
  );
};

// DOCUMENTS TO SIGN — every billing document generated for the hotel (any period), for the hotel's own admins, its
// chain's admins and users, and the partner. The hotel's admins download, sign and upload; the partner's admins
// countersign. ui-v2-billing-documents answers anyone who can reach the hotel and decides who may do what.
const HotelSignDocuments = ({ hotelId }) => {
  const [docs, setDocs] = useState(null);
  const [partnerId, setPartnerId] = useState(null);   // sent to the partner's admins only — for countersigning
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    billingService.listDocuments(hotelId)
      .then(res => { if (live) { setDocs(res.documents || []); setPartnerId(res.partnerId || null); } })
      .catch(err => { if (live) setError(apiErrorMessage(err, 'Could not load the documents to sign')); });
    return () => { live = false; };
  }, [hotelId]);

  return (
    <div className="billing-section">
      <div>
        <h3 className="billing-payer-title">Documents to sign</h3>
        <p className="billing-muted">Download each document, sign it and upload the signed copy here; the partner then countersigns it.</p>
      </div>
      {error ? <div className="acp-error-banner">{error}</div>
        : !docs ? <p className="billing-muted">Loading documents…</p>
          : docs.length === 0 ? <p className="billing-muted">No documents to sign.</p>
            : <DocumentRows hotelId={hotelId} partnerId={partnerId} docs={docs} onChange={setDocs} showPeriod />}
    </div>
  );
};

// FILL FROM QUICKBOOKS (superadmin only): copies the linked QuickBooks customer's contact, email, phone, address,
// country and VAT number into the form — the hub's field mapping is the accounts API's (billingrecords
// detailsFromCustomer). Nothing is saved until Save, which goes through the usual checks, audit and outbox.
const QuickBooksFill = ({ customerIds, onFill, disabled }) => {
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState(null);
  const [failed, setFailed] = useState(null);
  const fill = async (customerId) => {
    setBusy(customerId);
    setNote(null);
    setFailed(null);
    try {
      const res = await accountsAgentService.qboCustomerBilling(customerId);
      const filled = Object.keys(res.details || {});
      onFill(res.details || {});
      setNote(`Filled ${filled.length} field(s) from QuickBooks customer ${res.name || customerId} — check them, then Save.`
        + (res.unmapped && res.unmapped.country ? ` Its country "${res.unmapped.country}" has no code here: choose it.` : ''));
    } catch (err) {
      setFailed(apiErrorMessage(err, 'QuickBooks did not return that customer'));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="billing-qb-fill">
      {customerIds.map(id => (
        <button key={id} type="button" className="pp-criteria-add-btn sa-only" onClick={() => fill(id)} disabled={disabled || !!busy}
          title="Superadmin: replace these fields with the linked QuickBooks customer's details">
          {busy === id ? 'Reading QuickBooks…' : `Fill from QuickBooks${customerIds.length > 1 ? ` (${id})` : ''}`}
        </button>
      ))}
      {note && <p className="billing-muted">{note}</p>}
      {failed && <div className="acp-error-banner">{failed}</div>}
    </div>
  );
};

// TWO WAYS TO HOST IT. Without `mode` (partner settings, chain settings, the vendor page — screens where every tab
// saves itself) it keeps its own Save / Cancel. With `mode` (the hotel, chain and partner record pages) it follows the
// page like every other tab there: read only on the view page, and on the edit page saved by the page's own
// Update button through the ref — { pending() → null | { problem }, save() } — with no buttons of its own; the page's
// own Edit button is the way in.
const BillingTab = forwardRef(({ entity, entityId, mode }, ref) => {
  const { isSuperAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(toForm());
  const [billTo, setBillTo] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  // A HOTEL'S PAYER BY DATE (V625): the schedule as edited here — [{ _k, from, billTo, billToCredentialId }] — and
  // the period whose detail is shown under the list (its _k).
  const [periods, setPeriods] = useState([]);
  const [selected, setSelected] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const embedded = !!mode;
  // What may be changed HERE. Embedded on a view page nothing is — Edit Hotel / Edit Hotel Chain / Edit is the way in.
  const editable = !!(data && data.canEdit) && (!embedded || mode === 'edit');

  const apply = (res) => {
    setData(res);
    setForm(toForm(res.details));
    setBillTo(res.billTo || SELF[res.entity] || '');
    setVendorId(res.billToCredentialId ? String(res.billToCredentialId) : '');
    if (entity === 'hotel') setPeriods(savedPeriods(res));
  };

  // A new schedule: today's payer (the period covering today) is what billTo / vendorId — and so the payer details
  // and checks below — follow.
  const setSchedule = (list) => {
    setPeriods(list);
    const current = periodOn(withEnds(list));
    if (current) {
      setBillTo(current.billTo);
      setVendorId(current.billToCredentialId ? String(current.billToCredentialId) : '');
    }
    setSaved(false);
  };

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError(null);
    try {
      apply(await billingService.get(entity, entityId));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load billing details'));
    } finally {
      setLoading(false);
    }
  }, [entity, entityId]);

  useEffect(() => { load(); }, [load]);

  const openPeriod = (k) => setSelected(k);

  // ADD A BILLING TARGET — always a NEW period, after the last one: the day after its To when it has one, else today
  // (or the day after it starts, if that is later). The period before then ends the day before the new one starts —
  // its To follows from the new From — so the schedule never overlaps or leaves a gap.
  const lastPeriod = () => { const rows = withEnds(periods); return rows[rows.length - 1] || null; };
  const addPeriod = () => {
    const last = lastPeriod();
    const from = !last ? today()
      : last.to ? nextDay(last.to)
        : (today() > last.from ? today() : nextDay(last.from));
    const p = { _k: newPeriodKey(), from, billTo: 'HOTEL', billToCredentialId: null, ...PERIOD_DEFAULTS };
    setSchedule([...periods.map(x => (x._k === last._k ? { ...x, end: undefined } : x)), p]);
    setSelected(p._k);
  };

  // A period's To. On the last one it is that period's end, waiting for the target that follows; on any other it
  // moves the next period's start to the day after, so the two still meet.
  const updateTo = (k, to) => {
    const rows = withEnds(periods);
    const at = rows.findIndex(p => p._k === k);
    if (at < 0) return;
    const next = rows[at + 1];
    setSchedule(periods.map(p => {
      if (next && p._k === next._k) return { ...p, from: to ? nextDay(to) : p.from };
      if (!next && p._k === k) return { ...p, end: to || undefined };
      return p;
    }));
  };

  // ARRIVING FROM ADD ON THE VIEW PAGE (?add=1): the new period is added here and opened, and the parameter dropped
  // so a refresh does not add another. ?period=<from> opens that period (a link to one billing period).
  useEffect(() => {
    if (entity !== 'hotel' || !data) return;
    const params = new URLSearchParams(location.search);
    if (params.get('add') === '1') {
      params.delete('add');
      navigate({ search: params.toString() }, { replace: true });
      if (editable) addPeriod();
      return;
    }
    const want = params.get('period');
    const hit = want && periods.find(p => p.from === want);
    if (hit) openPeriod(hit._k);
  }, [data]); // eslint-disable-line

  // Everything the screen and the page's Update button both need to know, from the same state.
  const derive = () => {
    // WHO IS BILLED is the partner's (data.partnerView): the hotel's and chain's own users neither see the schedule nor
    // choose who pays — they keep their billing details, and the documents to sign.
    const hotelNonPartner = entity === 'hotel' && !!data && data.partnerView === false;
    const payerLevel = !!SELF[entity];
    const choosesPayer = payerLevel && !hotelNonPartner;
    const dated = entity === 'hotel' && !hotelNonPartner;
    const currentIsSelf = !payerLevel || billTo === SELF[entity];
    // Its own details are needed while it pays for ANY period of its schedule (a hotel), or for itself (the rest).
    const billsItself = dated ? periods.some(p => p.billTo === 'HOTEL') : currentIsSelf;
    const options = (data && data.options) || {};
    const status = currentIsSelf ? null : choiceStatus(billTo, vendorId, options);
    const ownMissing = missingBilling(form);
    // Every payer the schedule names that has not finished paying needs complete details (the lambda checks the same).
    const payerGap = dated
      ? withEnds(periods).filter(p => p.billTo !== 'HOTEL' && (!p.to || p.to >= today()))
        .map(p => choiceStatus(p.billTo, p.billToCredentialId, options)).find(st => st && st.missing.length > 0)
      : status && status.missing.length > 0 ? status : null;
    // Paying for itself means its own details must be complete; partners and vendors are only held to it
    // when someone is billed to them, which the lambda knows and reports on save.
    const problem = (dated && scheduleProblem(periods) ? `Billing: ${scheduleProblem(periods)}` : null)
      || ((payerLevel && billsItself && ownMissing.length > 0)
        ? `Billing: this ${entity} pays its own invoices — add the ${ownMissing.join(', ')}.`
        : !dated && billTo === 'VENDOR' && !vendorId
          ? 'Billing: choose the vendor this hotel is billed to.'
          : payerGap
            ? `Billing: ${payerGap.label} has no complete billing details — missing ${payerGap.missing.join(', ')}.`
            : null);
    const savedForm = toForm(data && data.details);
    const changed = !!data && (
      (billsItself && FIELDS.some(f => String(form[f.key] ?? '') !== String(savedForm[f.key] ?? '')))
      || (dated
        ? periodsKey(periods) !== periodsKey(savedPeriods(data))
        : choosesPayer && (billTo !== (data.billTo || SELF[entity])
          || (billTo === 'VENDOR' && vendorId !== String(data.billToCredentialId || '')))));
    return { choosesPayer, payerLevel, dated, currentIsSelf, billsItself, options, status, ownMissing, problem, changed };
  };

  // One save for both hosts. cardsFollowBillTo (hotel, V570): also move card payments off the chain's Stripe
  // customer onto the hotel's own.
  // `schedule` — a hotel's schedule to save instead of the one on screen (a removal, saved in the same click).
  const persist = async ({ cardsFollowBillTo = false, schedule = null } = {}) => {
    const { choosesPayer, dated, billsItself } = derive();
    const body = billsItself ? { details: form } : {};
    if (dated) {
      // The whole schedule, every time: the lambda replaces it and keeps hotel.bill_to on today's payer.
      // The last period's `to` is its end (V662; null runs on) — the lambda reads it there and nowhere else.
      body.periods = withEnds(schedule || periods).map((p, i, all) => ({
        from: p.from, billTo: p.billTo, billToCredentialId: p.billToCredentialId || null,
        ...Object.fromEntries(PERIOD_FIELDS.map(f => [f.key, (p[f.key] || '').trim() || null])),
        ...(i === all.length - 1 ? { to: p.to || null } : {}),
      }));
    } else if (choosesPayer) {
      body.billTo = billTo;
      if (billTo === 'VENDOR') body.billToCredentialId = parseInt(vendorId, 10);
    }
    if (cardsFollowBillTo) body.cardsFollowBillTo = true;
    apply(await billingService.save(entity, entityId, body));
  };

  // THE PAGE'S UPDATE BUTTON. pending() is null when nothing on this tab changed — an untouched Billing tab never
  // stops the page saving, even where the record's billing details are still incomplete. save() throws a readable
  // message, so the page shows it beside its own errors.
  useImperativeHandle(ref, () => ({
    pending: () => {
      if (!editable) return null;
      const { changed, problem } = derive();
      return changed ? { problem } : null;
    },
    save: async () => {
      if (!editable || !derive().changed) return;
      try {
        await persist();
      } catch (err) {
        throw new Error(apiErrorMessage(err, 'Failed to save billing details'));
      }
    },
  }));

  if (loading) return <p className="billing-muted">Loading billing details…</p>;
  if (!data) return error ? <div className="acp-error-banner">{error}</div> : null;

  const { choosesPayer, payerLevel, dated, currentIsSelf, billsItself, options, status, ownMissing, problem } = derive();
  // A period's payer in words, from the payers this hotel may choose.
  const periodLabel = (p) => {
    if (p.billTo === 'HOTEL') return 'This hotel';
    const st = choiceStatus(p.billTo, p.billToCredentialId, options);
    if (st) return st.label.charAt(0).toUpperCase() + st.label.slice(1);
    return p.billTo === 'VENDOR' ? 'A vendor no longer available' : p.billTo.charAt(0) + p.billTo.slice(1).toLowerCase();
  };
  // The saved payer's details are only meaningful while the choice on screen still matches it.
  const payerMatches = data.payer && billTo === data.billTo
    && (billTo !== 'VENDOR' || String(data.billToCredentialId) === vendorId);
  const blocked = !!problem;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setSaved(false);
  };

  const handleCancel = () => {
    apply(data);
    setError(null);
    setSaved(false);
  };

  const handleSave = async (opts) => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await persist(opts);
      setSaved(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to save billing details'));
    } finally {
      setSaving(false);
    }
  };

  // THE DETAIL OF ONE PERIOD (hotel) — and the same blocks for a chain / partner / vendor without a schedule.
  const rows = withEnds(periods);
  const sel = rows.find(p => p._k === selected) || null;
  const isFirst = !!sel && rows[0] && rows[0]._k === sel._k;
  const currentPeriod = periodOn(rows);
  const selIsCurrent = !!sel && !!currentPeriod && currentPeriod._k === sel._k;

  const updatePeriod = (k, patch) => setSchedule(periods.map(p => (p._k === k ? { ...p, ...patch } : p)));
  // The saved period this one is, by its start date — documents are made from what is saved, never the screen.
  const savedPeriodFrom = (p) => (savedPeriods(data).some(x => x.from === p.from) ? p.from : null);
  // Back to the list, keeping what was changed — the page's Update button saves it, with everything else.
  const closePeriod = () => { setError(null); setSelected(null); };
  // Taken out of the schedule on screen; Update saves it (the period before then runs on in its place).
  const removePeriod = (k) => { setSchedule(periods.filter(p => p._k !== k)); setSelected(null); };

  // A payer other than the hotel for a period that is not today's: what it lacks, and where its details are kept.
  const otherPayer = (p) => {
    const st = choiceStatus(p.billTo, p.billToCredentialId, options);
    const where = p.billTo === 'CHAIN' && options.chain ? { entity: 'chain', id: options.chain.id }
      : p.billTo === 'PARTNER' && options.partner ? { entity: 'partner', id: options.partner.id }
        : p.billTo === 'VENDOR' && p.billToCredentialId ? { entity: 'vendor', id: p.billToCredentialId } : null;
    const link = where && payerLink(where, data.canEditRestricted);
    return (
      <>
        <PayerProblem status={st} level={entity} />
        <p className="billing-muted">
          The invoices for this period go to {st ? st.label : 'this payer'}; its billing details are kept there.
          {link && <> <Link to={link} className="billing-payer-link">See them there</Link>.</>}
        </p>
      </>
    );
  };

  const ownDetails = () => (
        <>
          {data.cardsOnChain && data.billTo === billTo && (
            <div className="acp-error-banner">
              This hotel pays its own invoices, but its card payments still use its chain's Stripe customer and the
              cards saved there. Choose who pays above to move both, or move card payments to this hotel's own
              customer, where its chain's saved cards will no longer be offered.
              {editable && (
                <div>
                  <button type="button" className="pp-criteria-add-btn" onClick={() => handleSave({ cardsFollowBillTo: true })}
                    disabled={saving || blocked}>
                    Move card payments to this hotel
                  </button>
                </div>
              )}
            </div>
          )}
          {payerLevel && ownMissing.length > 0 && (
            <div className="acp-error-banner">
              This {entity} pays its own invoices, so its billing details are required — missing {ownMissing.join(', ')}.
            </div>
          )}
          <BillingFields
            values={form}
            onChange={handleChange}
            disabled={!editable}
            canEditRestricted={data.canEditRestricted}
            required={payerLevel}
          />
          <QuickBooksStatus qb={data.quickBooks} />
          {isSuperAdmin && editable && (data.quickBooks?.customerIds || []).length > 0 && (
            <QuickBooksFill
              customerIds={data.quickBooks.customerIds}
              disabled={saving}
              onFill={(details) => { setForm(prev => ({ ...prev, ...details })); setSaved(false); }}
            />
          )}
        </>
  );

  const payerDetails = () => (
        <>
          <PayerProblem status={status} level={entity} />
          {payerMatches ? (
            <div className="billing-section">
              <div className="billing-payer-header">
                <h3 className="billing-payer-title">
                  Billing details of the {data.payer.entity} {data.payer.name || ''}
                  {data.payer.via ? ` (via the chain ${data.payer.via.name})` : ''}
                </h3>
                {payerLink(data.payer, data.canEditRestricted) && (
                  <Link to={payerLink(data.payer, data.canEditRestricted)} className="billing-payer-link">Edit there</Link>
                )}
              </div>
              <BillingFields values={toForm(data.payer.details)} onChange={() => {}} disabled canEditRestricted={false} />
            </div>
          ) : data.billTo === billTo && !data.payer ? (
            <div className="acp-error-banner">
              This {entity} is set to be billed to its {billTo.toLowerCase()}, but none is linked. Choose who pays.
            </div>
          ) : !status || status.missing.length === 0 ? (
            <p className="billing-muted">
              Save to bill this {entity} to {status ? status.label : `the ${billTo.toLowerCase()}`}; its billing details will show here.
            </p>
          ) : null}
        </>
  );

  // The tab is embedded inside the hotel / chain / partner <form>: its buttons are type="button", and Enter
  // in one of its fields must not submit (and save) the parent form either.
  return (
    <div className="billing-tab" onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault(); }}>
      {!data.canEdit && (
        <p className="billing-muted">
          {entity === 'partner' || entity === 'vendor'
            ? 'Only a superadmin can change these billing details.'
            : 'Read only — changing billing details needs an administrator role on this property or its chain.'}
        </p>
      )}

      {choosesPayer && !dated && (
        <BillToChoice
          level={entity}
          billTo={billTo}
          vendorId={vendorId}
          options={options}
          onChange={({ billTo: b, vendorId: v }) => { setBillTo(b); setVendorId(v); setSaved(false); }}
          disabled={!editable}
        />
      )}

      {/* WHO IS BILLED, BY DATE (V625) — e.g. this hotel until 31 March, its vendor from 1 April. A row shows that
          period's detail below. Each period runs until the day before the next starts: no overlap, no gap. */}
      {dated && !sel && (
        <div className="billing-section partner-only-panel">
          {/* The title and its Add button on one row, as every list in the portal has them. */}
          <div className="billing-payer-header billing-list-header">
            <h3 className="billing-payer-title">Who is billed</h3>
            {/* Always adds a new period — here on the edit page, or on the view page by opening the edit page with it
                added. */}
            {data.canEdit && (
              <button type="button" className="pp-rules-add-btn"
                onClick={editable ? addPeriod
                  : () => {
                    // To the edit page with the new period added and open. ?returnTo= kept, so the hotel's Back still
                    // returns where the user started.
                    const back = new URLSearchParams(location.search).get('returnTo');
                    navigate(`/hotel/${entityId}/edit?tab=basic&sub=billing&add=1`
                      + (back ? `&returnTo=${encodeURIComponent(back)}` : ''));
                  }}>
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Add billing target
              </button>
            )}
          </div>
          <PeriodList periods={periods} labelOf={periodLabel} selectedKey={selected} onSelect={openPeriod} />
          <p className="billing-muted">
            Choose a billing period to see its details.
          </p>
        </div>
      )}

      {/* ONE PERIOD ON ITS OWN, in place of the list; Back returns to it. */}
      {dated && sel && (
        <div className="billing-section partner-only-panel">
          <button type="button" className="pp-back-btn" onClick={closePeriod}>← Back to who is billed</button>
          <div className="billing-payer-header">
            <h3 className="billing-payer-title">
              {periodLabel(sel)}
              {selIsCurrent && <span className="bsch-now">current</span>}
            </h3>
          </div>
          {/* The period's own fields — read only on the view page, like every field there. */}
          <div className="acp-row-2">
            <div className="acp-field">
              <label className="acp-label" htmlFor="billing-period-from">From</label>
              <input id="billing-period-from" type="date" className="acp-input" value={sel.from || ''} disabled={!editable}
                onChange={e => updatePeriod(sel._k, { from: e.target.value })} />
              <span className="acp-hint">
                {isFirst ? 'The first period also covers every date before it.' : 'The period before ends the day before this.'}
              </span>
            </div>
            <div className="acp-field">
              <label className="acp-label" htmlFor="billing-period-to">To</label>
              <input id="billing-period-to" type="date" className="acp-input" value={sel.to || ''} min={sel.from || undefined}
                disabled={!editable} onChange={e => updateTo(sel._k, e.target.value)} />
              <span className="acp-hint">
                {rows[rows.length - 1] && rows[rows.length - 1]._k === sel._k
                  ? (editable ? 'Empty: no end. Fill it in to end billing on that day.' : 'Empty: no end.')
                  : 'The next billing period starts the day after.'}
              </span>
            </div>
          </div>
          <div className="acp-row-2">
            <div className="acp-field">
              <label className="acp-label" htmlFor="billing-period-payer">Billed to</label>
              <select id="billing-period-payer" className="acp-select" value={valueOf(sel)} disabled={!editable}
                onChange={e => updatePeriod(sel._k, fromValue(e.target.value))}>
                {payerChoices(options).map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                {!payerChoices(options).some(c => c.value === valueOf(sel)) && (
                  <option value={valueOf(sel)}>{sel.billTo === 'VENDOR' ? 'A vendor no longer available' : sel.billTo}</option>
                )}
              </select>
            </div>
          </div>
          {/* The order form's own fields for this period (V663) — read only on the view page, like the rest. */}
          <div className="billing-grid">
            {PERIOD_FIELDS.map(f => (
              <div className="acp-field" key={f.key}>
                <label className="acp-label" htmlFor={`billing-period-${f.key}`}>{f.label}</label>
                <input id={`billing-period-${f.key}`} className="acp-input" type={f.type || 'text'} value={sel[f.key] || ''}
                  placeholder={editable && f.default ? `Blank: ${f.default}` : undefined} disabled={!editable}
                  onChange={e => updatePeriod(sel._k, { [f.key]: e.target.value })} />
              </div>
            ))}
          </div>
          {editable && (
            <>
              {scheduleProblem(periods) && <div className="acp-error-banner">{scheduleProblem(periods)}</div>}
              {periods.length > 1 && (
                <button type="button" className="pp-criteria-add-btn pp-criteria-add-btn-danger billing-remove-period"
                  onClick={() => removePeriod(sel._k)}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    <path d="M10 11v6M14 11v6"/>
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                  </svg>
                  Remove this period
                </button>
              )}
            </>
          )}
          {sel.billTo === 'HOTEL'
            ? ownDetails()
            : selIsCurrent
              ? payerDetails()
              : otherPayer(sel)}
          {/* Last on the period's screen, below the billing details. */}
          <PeriodDocuments hotelId={entityId} saved={savedPeriodFrom(sel)} unsaved={derive().changed} />
          {error && <div className="acp-error-banner">{error}</div>}
        </div>
      )}

      {/* DOCUMENTS TO SIGN — for everyone who can see the hotel, the partner's people included. */}
      {entity === 'hotel' && !sel && <HotelSignDocuments hotelId={entityId} />}

      {!dated && billsItself && ownDetails()}
      {!dated && !currentIsSelf && payerDetails()}

      {/* Its own Save, so on a record's view page too — no Edit needed (chain settings, the hotel's Basic › Billing). */}
      {data.canEditQuickBooks && (
        <QuickBooksCustomerField entity={entity} entityId={entityId} customerIds={data.quickBooks?.customerIds}
          onSaved={(res) => setData(d => ({ ...d, quickBooks: res.quickBooks, canEditQuickBooks: res.canEditQuickBooks }))} />
      )}

      {error && !sel && <div className="acp-error-banner">{error}</div>}
      {saved && <p className="billing-saved">Saved.</p>}

      {editable && !embedded && (
        <SaveCancel saving={saving} blocked={blocked} onCancel={handleCancel} onSave={() => handleSave()} />
      )}
    </div>
  );
});
BillingTab.displayName = 'BillingTab';

export default BillingTab;
